import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import type {
  BacktestRun,
  BacktestInvoiceRow,
  CalculatedRow,
  BacktestSummary,
} from "@/types/backtest";

const BATCH_SIZE = 500;

export function useBacktestRuns() {
  const { user } = useAuth();
  const qc = useQueryClient();

  const { data: runs = [], isLoading } = useQuery({
    queryKey: ["backtest-runs", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("backtest_runs")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  const createRun = useMutation({
    mutationFn: async ({
      name,
      clientName,
      rows,
      summary,
      file,
      onProgress,
    }: {
      name: string;
      clientName?: string | null;
      rows: CalculatedRow[];
      summary: BacktestSummary;
      file: File;
      onProgress?: (pct: number) => void;
    }) => {
      if (!user) throw new Error("Not authenticated");

      onProgress?.(5);

      // 1. Upload raw file to storage
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      const filePath = `${user.id}/${Date.now()}_${safeName}`;

      const { error: uploadError } = await supabase.storage
        .from("backtest-uploads")
        .upload(filePath, file, { upsert: false });

      if (uploadError) throw uploadError;
      onProgress?.(20);

      // 2. Create run record
      const { data: run, error: runError } = await supabase
        .from("backtest_runs")
        .insert({
          user_id: user.id,
          name,
          client_name: clientName?.trim() || null,
          status: "complete",
          file_path: filePath,
          file_name: file.name,
          file_size: file.size,
          date_range_start: summary.dateRangeStart,
          date_range_end: summary.dateRangeEnd,
          total_invoices: summary.totalInvoices,
          eligible_invoices: summary.eligibleInvoices,
          discounts_captured: summary.discountsCaptured,
          discounts_missed: summary.discountsMissed,
          total_missed_savings: summary.totalMissedSavings,
          total_captured_savings: summary.totalCapturedSavings,
          capture_rate: summary.captureRate,
        })
        .select()
        .single();

      if (runError) throw runError;
      onProgress?.(30);

      const createdRun = run;

      // 3. Insert invoice rows in batches
      const invoiceRows = rows.map((r) => ({
        run_id: createdRun.id,
        row_number: r.rowNumber,
        invoice_number: r.invoiceNumber,
        supplier_name: r.supplierName,
        invoice_date: r.invoiceDate,
        invoice_amount: r.invoiceAmount,
        payment_terms: r.paymentTerms,
        actual_payment_date: r.actualPaymentDate,
        discount_pct: r.parsedTerms?.discountPct ?? null,
        discount_days: r.parsedTerms?.discountDays ?? null,
        net_days: r.parsedTerms?.netDays ?? null,
        discount_deadline: r.discountDeadline,
        was_eligible: r.wasEligible,
        discount_captured: r.discountCaptured,
        missed_savings: r.missedSavings,
        captured_savings: r.capturedSavings,
        parse_error: r.parseError,
      }));

      const totalBatches = Math.ceil(invoiceRows.length / BATCH_SIZE);
      for (let i = 0; i < invoiceRows.length; i += BATCH_SIZE) {
        const batch = invoiceRows.slice(i, i + BATCH_SIZE);
        const { error: rowsError } = await supabase.from("backtest_invoice_rows").insert(batch);
        if (rowsError) throw rowsError;
        const batchIndex = Math.floor(i / BATCH_SIZE) + 1;
        onProgress?.(30 + Math.floor((batchIndex / totalBatches) * 65));
      }

      onProgress?.(100);
      return createdRun;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["backtest-runs", user?.id] });
    },
  });

  const deleteRun = useMutation({
    mutationFn: async (runId: string) => {
      // Delete file from storage first
      const run = runs.find((r) => r.id === runId);
      if (run?.file_path) {
        await supabase.storage.from("backtest-uploads").remove([run.file_path]);
      }
      const { error } = await supabase.from("backtest_runs").delete().eq("id", runId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["backtest-runs", user?.id] });
    },
  });

  return { runs, isLoading, createRun, deleteRun };
}

export function useBacktestRun(runId: string | undefined) {
  const { data: run, isLoading: runLoading } = useQuery({
    queryKey: ["backtest-run", runId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("backtest_runs")
        .select("*")
        .eq("id", runId)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!runId,
  });

  const { data: invoiceRows = [], isLoading: rowsLoading } = useQuery({
    queryKey: ["backtest-invoice-rows", runId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("backtest_invoice_rows")
        .select("*")
        .eq("run_id", runId)
        .order("row_number", { ascending: true });
      if (error) throw error;
      return data;
    },
    enabled: !!runId,
  });

  const getDownloadUrl = async (): Promise<string | null> => {
    if (!run?.file_path) return null;
    const { data, error } = await supabase.storage
      .from("backtest-uploads")
      .createSignedUrl(run.file_path, 60);
    if (error) return null;
    return data.signedUrl;
  };

  return { run, invoiceRows, isLoading: runLoading || rowsLoading, getDownloadUrl };
}
